/**
 * Financial Taxonomy - Standardized Specializations
 * 
 * This file defines the standardized taxonomy used across the platform
 */
import { 
  Target, 
  TrendingUp, 
  PieChart, 
  BarChart3, 
  Lightbulb, 
  FileText, 
  LineChart, 
  Calendar, 
  DollarSign, 
  Shield, 
  UserCheck 
} from "lucide-react";

/**
 * Standardized Financial Specializations
 * 
 * Used for:
 * - Investor: investment_goals field
 * - Advisor: specializations field
 * - Analyst: investment_focus field
 * 
 * IMPORTANT: Keep this list synchronized with any backend enum/validation
 */
export const FINANCIAL_SPECIALIZATIONS = [
  // Tier 1: Core Financial Planning (Most Common)
  "Retirement Planning",
  "Tax Planning & Optimization",
  "Wealth Management",
  "Investment Management",
  "Estate Planning",
  
  // Tier 2: Risk & Protection
  "Risk Management & Insurance",
  "Capital Preservation",
  
  // Tier 3: Growth & Income Strategies
  "Long-term Growth Strategies",
  "Income Generation Strategies",
  "Portfolio Diversification",
  
  // Tier 4: Specialized Areas
  "Business Succession Planning",
  "Education Planning",
  "Charitable Giving & Philanthropy",
  "Alternative Investments",
  "ESG/Sustainable Investing",
] as const;

export type FinancialSpecialization = typeof FINANCIAL_SPECIALIZATIONS[number];

/**
 * Risk Tolerance Levels
 * Standardized across all user types
 */
export const RISK_TOLERANCE_LEVELS = [
  { value: "CONSERVATIVE", label: "Conservative - Minimize risk", description: "Focus on capital preservation and steady income" },
  { value: "MODERATE", label: "Moderate - Balanced approach", description: "Mix of growth and stability" },
  { value: "AGGRESSIVE", label: "Aggressive - Maximize returns", description: "Higher risk tolerance for potential growth" },
] as const;

export type RiskTolerance = typeof RISK_TOLERANCE_LEVELS[number]['value'];

/**
 * Investment Time Horizons
 */
export const TIME_HORIZONS = [
  { value: "short", label: "Short-term (< 3 years)", description: "Near-term goals and liquidity needs" },
  { value: "medium", label: "Medium-term (3-10 years)", description: "Mid-range planning and growth" },
  { value: "long", label: "Long-term (10+ years)", description: "Retirement and legacy planning" },
] as const;

export type TimeHorizon = typeof TIME_HORIZONS[number]['value'];

/**
 * Certification Types
 * Professional credentials recognized on the platform
 */
export const CERTIFICATIONS = [
  { value: "CFA", label: "CFA - Chartered Financial Analyst", premium: true },
  { value: "CFP", label: "CFP - Certified Financial Planner", premium: true },
  { value: "CPA", label: "CPA - Certified Public Accountant", premium: false },
  { value: "ChFC", label: "ChFC - Chartered Financial Consultant", premium: false },
  { value: "CLU", label: "CLU - Chartered Life Underwriter", premium: false },
  { value: "CIMA", label: "CIMA - Certified Investment Management Analyst", premium: false },
  { value: "PFS", label: "PFS - Personal Financial Specialist", premium: false },
  { value: "MBA", label: "MBA - Master of Business Administration", premium: false },
  { value: "JD", label: "JD - Juris Doctor (Law Degree)", premium: false },
] as const;

export type Certification = typeof CERTIFICATIONS[number]['value'];


/**
 * Certification Filter Options
 * Used to filter matches by professional certifications
 */
export const CERTIFICATION_FILTER_OPTIONS = [
  { value: "CFA", label: "CFA - Chartered Financial Analyst" },
  { value: "CFP", label: "CFP - Certified Financial Planner" },
  { value: "CPA", label: "CPA - Certified Public Accountant" },
  { value: "ChFC", label: "ChFC - Chartered Financial Consultant" },
  { value: "CLU", label: "CLU - Chartered Life Underwriter" },
  { value: "CIMA", label: "CIMA - Certified Investment Management Analyst" },
  { value: "PFS", label: "PFS - Personal Financial Specialist" },
  { value: "MBA", label: "MBA - Master of Business Administration" },
  { value: "JD", label: "JD - Juris Doctor (Law Degree)" },
] as const;

export type CertificationFilterValue = typeof CERTIFICATION_FILTER_OPTIONS[number]['value'];


/**
 * Research Styles (for Analysts)
 */
export const RESEARCH_STYLES = [
  { value: "fundamental", label: "Fundamental Analysis", description: "Deep dive into financials, business models, and competitive positioning" },
  { value: "technical", label: "Technical Analysis", description: "Chart patterns, trends, and market indicators" },
  { value: "quantitative", label: "Quantitative Analysis", description: "Data-driven models and statistical methods" },
  { value: "macro", label: "Macro-economic Analysis", description: "Economic trends, policy impacts, and market cycles" },
  { value: "esg", label: "ESG Analysis", description: "Environmental, social, and governance factors" },
] as const;

export type ResearchStyle = typeof RESEARCH_STYLES[number]['value'];

/**
 * Sector Interests (for Investors & Analysts)
 */
export const SECTORS = [
  "Technology",
  "Healthcare",
  "Financial Services",
  "Consumer Goods",
  "Energy",
  "Real Estate",
  "Industrials",
  "Materials",
  "Utilities",
  "Telecommunications",
  "Consumer Services",
  "Aerospace & Defense",
  "Finance",
  "Commodities",
  "Diversified",
  "Fixed Income",
  "Growth",
  "International",
  "Broad Market",
] as const;

export type Sector = typeof SECTORS[number];

/**
 * Specialization Filter Options
 * Used to filter matches by advisor/analyst specializations
 * VALUES MUST MATCH FINANCIAL_SPECIALIZATIONS for proper filtering
 */
export const SPECIALIZATION_FILTER_OPTIONS = [
  { value: "all", label: "All Specializations" },
  { value: "Retirement Planning", label: "Retirement Planning" },
  { value: "Tax Planning & Optimization", label: "Tax Planning & Optimization" },
  { value: "Wealth Management", label: "Wealth Management" },
  { value: "Investment Management", label: "Investment Management" },
  { value: "Estate Planning", label: "Estate Planning" },
  { value: "ESG/Sustainable Investing", label: "ESG/Sustainable Investing" },
  { value: "Long-term Growth Strategies", label: "Growth Strategies" },
  { value: "Income Generation Strategies", label: "Income Strategies" },
  { value: "Alternative Investments", label: "Alternative Investments" },
  { value: "Risk Management & Insurance", label: "Risk Management & Insurance" },
  { value: "Capital Preservation", label: "Capital Preservation" },
] as const;

export type SpecializationFilterValue = typeof SPECIALIZATION_FILTER_OPTIONS[number]['value'];

/**
 * Location Filter Options
 * Common US cities/regions for filtering matches
 * Format: "city, state, country code" (ISO 3166-1 alpha-3)
 */
export const LOCATION_FILTER_OPTIONS = [
  { value: "all", label: "All Locations" },
  { value: "Boston, MA, USA", label: "Boston, MA, USA" },
  { value: "San Francisco, CA, USA", label: "San Francisco, CA, USA" },
  { value: "New York, NY, USA", label: "New York, NY, USA" },
  { value: "Chicago, IL, USA", label: "Chicago, IL, USA" },
  { value: "Seattle, WA, USA", label: "Seattle, WA, USA" },
  { value: "Austin, TX, USA", label: "Austin, TX, USA" },
  { value: "Miami, FL, USA", label: "Miami, FL, USA" },
  { value: "Washington, DC, USA", label: "Washington, DC, USA" },
  { value: "Los Angeles, CA, USA", label: "Los Angeles, CA, USA" },
  { value: "Denver, CO, USA", label: "Denver, CO, USA" },
  { value: "Toronto, ON, CAN", label: "Toronto, ON, CAN" },
  { value: "Vancouver, BC, CAN", label: "Vancouver, BC, CAN" },
  { value: "London, ENG, GBR", label: "London, ENG, GBR" },
  { value: "Singapore, SGP, SGP", label: "Singapore, SGP, SGP" },
  { value: "custom", label: "Custom Location (Enter manually)" },
] as const;

export type LocationFilterValue = typeof LOCATION_FILTER_OPTIONS[number]['value'];

/**
 * Rating Filter Options
 * Minimum star rating filter (1-5 stars)
 */
export const RATING_FILTER_OPTIONS = [
  { value: "0", label: "Any Rating", stars: 0 },
  { value: "1", label: "1+ ⭐", stars: 1 },
  { value: "2", label: "2+ ⭐⭐", stars: 2 },
  { value: "3", label: "3+ ⭐⭐⭐", stars: 3 },
  { value: "4", label: "4+ ⭐⭐⭐⭐", stars: 4 },
  { value: "5", label: "5 ⭐⭐⭐⭐⭐", stars: 5 },
] as const;

export type RatingFilterValue = typeof RATING_FILTER_OPTIONS[number]['value'];

export const BENCHMARK_SYMBOLS = [
    ["S&P 500", "S&P 500"],
    ["S&P", "S&P"],
    ["Nasdaq", "Nasdaq"],
    ["Nasdaq Composite", "Nasdaq Composite"],
    ["Russell 1000", "Russell 1000"],
    ["Russell 2000", "Russell 2000"],
    ["Dow Jones", "Dow Jones"],
    ["Dow", "Dow"],
    ["NYSE Composite", "NYSE Composite"],
    ["Wilshire 5000", "Wilshire 5000"],
    ["FTSE 100", "FTSE 100"],
  ]

  export type BenchmarkSymbol = typeof BENCHMARK_SYMBOLS[number][1];


/**
 * Match Score Slider Configuration
 */
export const MATCH_SCORE_SLIDER = {
  min: 0,
  max: 100,
  step: 5,
  defaultValue: 70,
} as const;

/**
 * Helper Functions
 */

/**
 * Get description for a specialization
 */
export function getSpecializationDescription(spec: FinancialSpecialization): string {
  const descriptions: Record<FinancialSpecialization, string> = {
    "Retirement Planning": "401(k), IRA, pension planning, and retirement income strategies",
    "Tax Planning & Optimization": "Tax-loss harvesting, tax-advantaged investing, and year-end strategies",
    "Wealth Management": "Comprehensive planning for high net worth individuals and families",
    "Investment Management": "Portfolio construction, asset allocation, and rebalancing",
    "Estate Planning": "Wills, trusts, beneficiary planning, and legacy strategies",
    "Risk Management & Insurance": "Life, disability, and long-term care insurance planning",
    "Capital Preservation": "Conservative strategies focused on principal protection",
    "Long-term Growth Strategies": "Equity investing and growth-oriented portfolio management",
    "Income Generation Strategies": "Dividend investing, bonds, and income-producing assets",
    "Portfolio Diversification": "Multi-asset strategies and geographic diversification",
    "Business Succession Planning": "Exit strategies, business valuation, and ownership transition",
    "Education Planning": "529 plans, college savings, and student loan optimization",
    "Charitable Giving & Philanthropy": "Donor-advised funds, charitable trusts, and impact investing",
    "Alternative Investments": "Private equity, real estate, hedge funds, and commodities",
    "ESG/Sustainable Investing": "Environmental, social, and governance-focused investments",
  };
  
  return descriptions[spec] || "";
}

/**
 * Get premium certifications only
 */
export function getPremiumCertifications() {
  return CERTIFICATIONS.filter(cert => cert.premium);
}

/**
 * Check if a certification is premium
 */
export function isPremiumCertification(certValue: string): boolean {
  return CERTIFICATIONS.some(cert => cert.value === certValue && cert.premium);
}

/**
 * Format array of specializations as comma-separated string
 * (for backend compatibility)
 */
export function formatSpecializationsForBackend(specs: string[]): string {
  return specs.join(", ");
}

/**
 * Parse comma-separated string to array of specializations
 * (from backend)
 */
export function parseSpecializationsFromBackend(specsString: string): string[] {
  if (!specsString) return [];
  return specsString.split(",").map(s => s.trim()).filter(Boolean);
}

/**
 * Validate specialization against known list
 */
export function isValidSpecialization(spec: string): boolean {
  return FINANCIAL_SPECIALIZATIONS.includes(spec as FinancialSpecialization);
}

/**
 * Get matching categories for analytics
 */
export function getSpecializationCategory(spec: FinancialSpecialization): string {
  const tier1 = [
    "Retirement Planning",
    "Tax Planning & Optimization",
    "Wealth Management",
    "Investment Management",
    "Estate Planning",
  ];
  
  const tier2 = [
    "Risk Management & Insurance",
    "Capital Preservation",
  ];
  
  const tier3 = [
    "Long-term Growth Strategies",
    "Income Generation Strategies",
    "Portfolio Diversification",
  ];
  
  if (tier1.includes(spec)) return "Core Financial Planning";
  if (tier2.includes(spec)) return "Risk & Protection";
  if (tier3.includes(spec)) return "Growth & Income";
  return "Specialized Services";
}

/**
 * Get recommended specializations based on risk tolerance
 */
export function getRecommendedSpecializations(riskTolerance: RiskTolerance): FinancialSpecialization[] {
  const recommendations: Record<RiskTolerance, FinancialSpecialization[]> = {
    CONSERVATIVE: [
      "Capital Preservation",
      "Income Generation Strategies",
      "Risk Management & Insurance",
      "Retirement Planning",
      "Estate Planning",
    ],
    MODERATE: [
      "Wealth Management",
      "Portfolio Diversification",
      "Investment Management",
      "Retirement Planning",
      "Tax Planning & Optimization",
    ],
    AGGRESSIVE: [
      "Long-term Growth Strategies",
      "Alternative Investments",
      "Investment Management",
      "ESG/Sustainable Investing",
      "Portfolio Diversification",
    ],
  };
  
  return recommendations[riskTolerance] || [];
}

/**
 * Sector Filter Options
 * Used to filter matches by sector interests/focus areas
 */
export const SECTOR_FILTER_OPTIONS = [
  { value: "technology", label: "Technology" },
  { value: "healthcare", label: "Healthcare" },
  { value: "financial_services", label: "Financial Services" },
  { value: "consumer_goods", label: "Consumer Goods" },
  { value: "energy", label: "Energy" },
  { value: "real_estate", label: "Real Estate" },
  { value: "industrials", label: "Industrials" },
  { value: "materials", label: "Materials" },
  { value: "utilities", label: "Utilities" },
  { value: "telecommunications", label: "Telecommunications" },
  { value: "consumer_services", label: "Consumer Services" },
  { value: "aerospace_defense", label: "Aerospace & Defense" },
] as const;

export type SectorFilterValue = typeof SECTOR_FILTER_OPTIONS[number]['value'];

/**
 * Investment Experience Levels
 */
export const INVESTMENT_EXPERIENCE_LEVELS = [
  { key: "beginner", value: "0-2", details: "Beginner (0-2 years)" },
  { key: "intermediate", value: "3-5", details: "Intermediate (3-5 years)" },
  { key: "experienced", value: "6-10", details: "Experienced (6-10 years)" },
  { key: "expert", value: "10+", details: "Expert (10+ years)" },
] as const;

export type InvestmentExperienceKey = typeof INVESTMENT_EXPERIENCE_LEVELS[number]['key'];

/**
 * Investment Strategies (for Investors)
 * 
 * Represents the investor's preferred investment approach/philosophy.
 * Maps to: Investor.investment_strategy field
 * 
 * This is different from:
 * - investment_goals (WHAT outcomes they want: retirement, tax planning, etc.)
 * - sector_interests (WHERE they want to invest: technology, healthcare, etc.)
 * - investment_strategy (HOW they approach investing: buy-and-hold, active trading, etc.)
 */
export const INVESTMENT_STRATEGIES = [
  // Core Strategies
  { 
    value: "buy_and_hold", 
    label: "Buy and Hold", 
    description: "Long-term investment in quality assets with minimal trading",
    riskProfile: "moderate",
    timeHorizon: "long"
  },
  { 
    value: "value_investing", 
    label: "Value Investing", 
    description: "Seek undervalued assets with strong fundamentals",
    riskProfile: "moderate",
    timeHorizon: "long"
  },
  { 
    value: "growth_investing", 
    label: "Growth Investing", 
    description: "Focus on companies with high growth potential",
    riskProfile: "aggressive",
    timeHorizon: "long"
  },
  { 
    value: "income_investing", 
    label: "Income Investing", 
    description: "Prioritize dividend-paying stocks and bonds for steady income",
    riskProfile: "conservative",
    timeHorizon: "medium"
  },
  { 
    value: "index_investing", 
    label: "Index/Passive Investing", 
    description: "Track market indexes with low-cost ETFs and mutual funds",
    riskProfile: "moderate",
    timeHorizon: "long"
  },
  
  // Active Strategies
  { 
    value: "active_trading", 
    label: "Active Trading", 
    description: "Frequent buying and selling to capitalize on market movements",
    riskProfile: "aggressive",
    timeHorizon: "short"
  },
  { 
    value: "momentum_investing", 
    label: "Momentum Investing", 
    description: "Invest in assets showing strong recent performance trends",
    riskProfile: "aggressive",
    timeHorizon: "short"
  },
  { 
    value: "swing_trading", 
    label: "Swing Trading", 
    description: "Hold positions for days to weeks to capture price swings",
    riskProfile: "aggressive",
    timeHorizon: "short"
  },
  
  // Diversified Strategies
  { 
    value: "balanced_portfolio", 
    label: "Balanced Portfolio", 
    description: "Mix of stocks, bonds, and other assets for diversification",
    riskProfile: "moderate",
    timeHorizon: "medium"
  },
  { 
    value: "asset_allocation", 
    label: "Strategic Asset Allocation", 
    description: "Systematic allocation across asset classes based on goals",
    riskProfile: "moderate",
    timeHorizon: "long"
  },
  { 
    value: "tactical_allocation", 
    label: "Tactical Asset Allocation", 
    description: "Actively adjust allocation based on market conditions",
    riskProfile: "moderate",
    timeHorizon: "medium"
  },
  
  // Specialized Strategies
  { 
    value: "esg_investing", 
    label: "ESG/Impact Investing", 
    description: "Focus on environmental, social, and governance criteria",
    riskProfile: "moderate",
    timeHorizon: "long"
  },
  { 
    value: "contrarian_investing", 
    label: "Contrarian Investing", 
    description: "Invest against prevailing market sentiment",
    riskProfile: "aggressive",
    timeHorizon: "long"
  },
  { 
    value: "dollar_cost_averaging", 
    label: "Dollar-Cost Averaging", 
    description: "Regular fixed investments regardless of market conditions",
    riskProfile: "conservative",
    timeHorizon: "long"
  },
  
  // Alternative Strategies
  { 
    value: "options_strategies", 
    label: "Options Trading", 
    description: "Use options for hedging or speculative positions",
    riskProfile: "aggressive",
    timeHorizon: "short"
  },
  { 
    value: "dividend_growth", 
    label: "Dividend Growth", 
    description: "Focus on companies with consistent dividend increases",
    riskProfile: "moderate",
    timeHorizon: "long"
  },
  { 
    value: "alternative_investments", 
    label: "Alternative Investments", 
    description: "Real estate, commodities, private equity, hedge funds",
    riskProfile: "aggressive",
    timeHorizon: "long"
  },
] as const;

export type InvestmentStrategy = typeof INVESTMENT_STRATEGIES[number]['value'];

/**
 * Fee Structures for Financial Advisors
 * Different pricing models used by financial advisors
 */
export const ADVISOR_FEE_TYPES = [
  {
    value: "aum",
    label: "Assets Under Management (AUM)",
    description: "Percentage of total assets managed, charged annually",
    typicalRange: "0.25% - 2.00%",
    billingFrequency: "MONTHLY",
    bestFor: "Clients with significant investable assets",
    pros: ["Aligned incentives", "Scales with portfolio size", "Comprehensive service"],
    cons: ["Can be expensive for large portfolios", "May discourage certain investments"],
  },
  {
    value: "flat_fee",
    label: "Flat Fee / Retainer",
    description: "Fixed annual or monthly fee regardless of asset size",
    typicalRange: "$2,000 - $10,000/year",
    billingFrequency: "MONTHLY",
    bestFor: "Clients seeking predictable costs",
    pros: ["Predictable costs", "No asset bias", "Transparent pricing"],
    cons: ["May not scale with complexity", "Upfront commitment required"],
  },
  {
    value: "hourly",
    label: "Hourly Rate",
    description: "Pay per hour for specific financial planning services",
    typicalRange: "$150 - $500/hour",
    billingFrequency: "HOURLY",
    bestFor: "One-time consultations or specific projects",
    pros: ["Pay only for time used", "Good for specific needs", "No ongoing commitment"],
    cons: ["Costs can be unpredictable", "May feel rushed", "No ongoing relationship"],
  },
  {
    value: "project_based",
    label: "Project-Based Fee",
    description: "Fixed fee for specific deliverables (financial plan, estate plan, etc.)",
    typicalRange: "$1,000 - $5,000 per project",
    billingFrequency: "ONE_TIME",
    bestFor: "Specific financial planning needs",
    pros: ["Clear scope and cost", "Defined deliverables", "One-time engagement"],
    cons: ["No ongoing support", "May need multiple projects", "Limited relationship"],
  },
  {
    value: "commission",
    label: "Commission-Based",
    description: "Compensation from product sales (investments, insurance)",
    typicalRange: "1% - 5% per transaction",
    billingFrequency: "TRANSACTIONAL",
    bestFor: "Product-focused services",
    pros: ["No upfront cost", "Access to products", "Transaction-based"],
    cons: ["Potential conflicts of interest", "May push certain products", "Hidden fees"],
  },
  {
    value: "performance_based",
    label: "Performance-Based Fee",
    description: "Fee based on portfolio performance or returns achieved",
    typicalRange: "10% - 20% of gains",
    billingFrequency: "PERFORMANCE_BASED",
    bestFor: "High net worth investors seeking aggressive growth",
    pros: ["Aligned with returns", "Only pay when profitable", "Incentivized performance"],
    cons: ["May encourage excessive risk", "Complex calculations", "Regulatory restrictions"],
  },
  {
    value: "hybrid",
    label: "Hybrid / Combination",
    description: "Combination of multiple fee structures (e.g., AUM + flat fee)",
    typicalRange: "Varies by combination",
    billingFrequency: "COMBINED",
    bestFor: "Complex financial situations",
    pros: ["Flexible pricing", "Customizable", "Can balance different needs"],
    cons: ["More complex to understand", "Potentially higher overall cost", "Requires careful evaluation"],
  },
  {
    value: "subscription",
    label: "Subscription / Membership",
    description: "Monthly or annual subscription for ongoing access to services",
    typicalRange: "$50 - $500/month",
    billingFrequency: "MONTHLY",
    bestFor: "Ongoing financial guidance and support",
    pros: ["Affordable monthly cost", "Continuous support", "Cancel anytime"],
    cons: ["Adds up over time", "May include unused services", "Varying service levels"],
  },
  {
    value: "flexible",
    label: "Flexible / To Be Discussed",
    description: "Custom fee structure to be negotiated based on specific needs",
    typicalRange: "Variable",
    billingFrequency: "FLEXIBLE",
    bestFor: "Unique financial situations",
    pros: ["Highly customizable", "Tailored to value", "Negotiable"],
    cons: ["Requires discussion", "Less predictable initially", "No standard benchmark"],
  },
  {
    value: "engagement_agreement",
    label: "As per Engagement Agreement",
    description: "Fee structure defined in the formal engagement agreement between parties",
    typicalRange: "As agreed",
    billingFrequency: "FLEXIBLE",
    bestFor: "Established long-term relationships",
    pros: ["Formalized terms", "Customized to relationship", "Legally binding"],
    cons: ["Requires existing agreement", "Terms may vary", "Less transparent initially"],
  },
] as const;

export type AdvisorFeeType = typeof ADVISOR_FEE_TYPES[number]['value'];

/**
 * Research Request Types (for Analysts)
 */
export const RESEARCH_REQUEST_TYPES = [
  { value: "stock_research", label: "Stock Research", icon: Target, fee: "$500 - $1,500" },
  { value: "sector_analysis", label: "Sector Analysis", icon: TrendingUp, fee: "$1,000 - $3,000" },
  { value: "portfolio_review", label: "Portfolio Review", icon: PieChart, fee: "$2,000 - $5,000" },
  { value: "market_outlook", label: "Market Outlook", icon: BarChart3, fee: "$1,500 - $4,000" },
  { value: "custom", label: "Custom Research", icon: Lightbulb, fee: "Quote-based" },
] as const;

/**
 * Advising Request Types (for Advisors)
 */
export const ADVISING_REQUEST_TYPES = [
  { value: "comprehensive_financial_planning", label: "Comprehensive Financial Planning", icon: FileText, fee: "$2,000 - $5,000" },
  { value: "investment_portfolio_management", label: "Investment Portfolio Management", icon: LineChart, fee: "0.75% - 1.25% AUM annually" },
  { value: "retirement_income_planning", label: "Retirement Income Planning", icon: Calendar, fee: "$1,500 - $3,500" },
  { value: "tax_preparation_strategy", label: "Tax Preparation & Strategy", icon: DollarSign, fee: "$1,000 - $3,000" },
  { value: "estate_planning_services", label: "Estate Planning Services", icon: Shield, fee: "$2,500 - $5,000" },
  { value: "insurance_analysis_planning", label: "Insurance Analysis & Planning", icon: UserCheck, fee: "$500 - $1,500" },
  { value: "business_financial_planning", label: "Business Financial Planning", icon: Target, fee: "$2,000 - $4,000" },
  { value: "education_funding_strategies", label: "Education Funding Strategies", icon: TrendingUp, fee: "$1,000 - $2,500" },
  { value: "real_estate_investment_consulting", label: "Real Estate Investment Consulting", icon: PieChart, fee: "$1,500 - $3,000" },
  { value: "charitable_giving_strategies", label: "Charitable Giving Strategies", icon: Lightbulb, fee: "$500 - $2,000" },
  { value: "cash_flow_budgeting", label: "Cash Flow & Budgeting", icon: BarChart3, fee: "$500 - $1,500" },
  { value: "debt_management", label: "Debt Management", icon: DollarSign, fee: "$500 - $2,000" },
] as const;

/**
 * Fee Structures for Financial Analysts
 * Different pricing models used by financial analysts for research and insights
 */
export const ANALYST_FEE_TYPES = [
  {
    value: "per_request",
    label: "Per Request / Per Report",
    description: "One-time fee for individual research reports or analysis",
    typicalRange: "$500 - $5,000 per report",
    billingFrequency: "ONE_TIME",
    bestFor: "Occasional research needs",
    pros: ["Pay only for what you need", "No commitment", "Flexibility"],
    cons: ["Can be expensive for frequent use", "No relationship discount", "Variable quality"],
  },
  {
    value: "subscription",
    label: "Subscription / Recurring",
    description: "Monthly or annual subscription for ongoing research access",
    typicalRange: "$200 - $2,000/month",
    billingFrequency: "MONTHLY",
    bestFor: "Regular research consumers",
    pros: ["Predictable costs", "Unlimited or allocated requests", "Priority access"],
    cons: ["Pay even if not using", "May have request limits", "Commitment required"],
  },
  {
    value: "tiered_subscription",
    label: "Tiered Subscription",
    description: "Multiple subscription levels with different features and request limits",
    typicalRange: "Basic: $99/mo, Pro: $299/mo, Enterprise: $999/mo",
    billingFrequency: "MONTHLY",
    bestFor: "Scalable research needs",
    pros: ["Choose appropriate tier", "Upgrade/downgrade flexibility", "Clear pricing"],
    cons: ["May outgrow tier quickly", "Features locked to tiers", "Complexity in choosing"],
  },
  {
    value: "retainer",
    label: "Retainer / Advisory",
    description: "Fixed monthly fee for dedicated analyst availability and priority service",
    typicalRange: "$2,000 - $10,000/month",
    billingFrequency: "MONTHLY",
    bestFor: "Institutional clients or heavy users",
    pros: ["Dedicated attention", "Priority response", "Custom research"],
    cons: ["High upfront cost", "Long-term commitment", "May pay for unused capacity"],
  },
  {
    value: "consulting_hourly",
    label: "Consulting Hourly Rate",
    description: "Hourly rate for custom consulting and analysis work",
    typicalRange: "$200 - $1,000/hour",
    billingFrequency: "HOURLY",
    bestFor: "Custom deep-dive analysis",
    pros: ["Pay for exact time", "Highly customized", "Expert attention"],
    cons: ["Unpredictable costs", "Can get expensive quickly", "Time tracking overhead"],
  },
  {
    value: "project_based",
    label: "Project-Based Fee",
    description: "Fixed fee for specific research projects or deliverables",
    typicalRange: "$5,000 - $50,000 per project",
    billingFrequency: "ONE_TIME",
    bestFor: "Large-scale research initiatives",
    pros: ["Fixed scope and price", "Defined deliverables", "Comprehensive analysis"],
    cons: ["Large upfront commitment", "Scope creep issues", "Long delivery times"],
  },
  {
    value: "hybrid",
    label: "Hybrid Model",
    description: "Combination of subscription base + per-request fees",
    typicalRange: "$500/mo base + $200-$1,000 per request",
    billingFrequency: "MONTHLY",
    bestFor: "Mix of regular and custom research needs",
    pros: ["Base access + flexibility", "Cost-effective for regular users", "Balanced approach"],
    cons: ["Complex pricing", "Still requires budgeting", "May not suit all patterns"],
  },
  {
    value: "data_feed",
    label: "Data Feed / API Access",
    description: "Subscription to raw data feeds or API access for automated analysis",
    typicalRange: "$1,000 - $10,000/month",
    billingFrequency: "MONTHLY",
    bestFor: "Quantitative firms and algorithms",
    pros: ["Real-time data", "Automated integration", "High volume access"],
    cons: ["Requires technical setup", "Expensive", "Data processing needed"],
  },
  {
    value: "freemium",
    label: "Freemium Model",
    description: "Free basic access with premium features requiring payment",
    typicalRange: "Free tier + $50 - $500/mo premium",
    billingFrequency: "MONTHLY",
    bestFor: "Trying before committing",
    pros: ["Try before buying", "Low barrier to entry", "Gradual commitment"],
    cons: ["Limited free features", "Upselling pressure", "May lack depth in free tier"],
  },
  {
    value: "flexible",
    label: "Flexible / Negotiable",
    description: "Pricing determined on a case-by-case basis",
    typicalRange: "Varies by scope",
    billingFrequency: "FLEXIBLE",
    bestFor: "Ad-hoc or unique research needs",
    pros: ["Fair pricing for unique tasks", "Open to negotiation", "Scope-based"],
    cons: ["Undefined upfront cost", "Requires proposal/quote", "Comparison is difficult"],
  },
  {
    value: "engagement_agreement",
    label: "As per Engagement Agreement",
    description: "Fee structure defined in the formal engagement agreement between parties",
    typicalRange: "As agreed",
    billingFrequency: "FLEXIBLE",
    bestFor: "Established long-term relationships",
    pros: ["Formalized terms", "Customized to relationship", "Legally binding"],
    cons: ["Requires existing agreement", "Terms may vary", "Less transparent initially"],
  },
] as const;

export type AnalystFeeType = typeof ANALYST_FEE_TYPES[number]['value'];

/**
 * Billing Frequencies
 * Standardized billing frequencies used across the platform
 */
export const BILLING_FREQUENCY = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "ANNUALLY", label: "Annually" },
  { value: "HOURLY", label: "Hourly" },
  { value: "ONE_TIME", label: "One-Time" },
  { value: "TRANSACTIONAL", label: "Transactional" },
  { value: "PERFORMANCE_BASED", label: "Performance-Based" },
  { value: "COMBINED", label: "Combined" },
  { value: "FLEXIBLE", label: "Flexible / Negotiable" },
] as const;

export type BillingFrequencyValue = typeof BILLING_FREQUENCY[number]['value'];

/**
 * Fee Structure Helper Functions
 */

/**
 * Get fee type details for advisors
 */
export function getAdvisorFeeTypeDetails(feeType: AdvisorFeeType) {
  return ADVISOR_FEE_TYPES.find(f => f.value === feeType);
}

/**
 * Get fee type details for analysts
 */
export function getAnalystFeeTypeDetails(feeType: AnalystFeeType) {
  return ANALYST_FEE_TYPES.find(f => f.value === feeType);
}

/**
 * Format fee type label for display
 */
export function formatFeeTypeLabel(feeType: string, role: 'advisor' | 'analyst'): string {
  if (role === 'advisor') {
    const details = ADVISOR_FEE_TYPES.find(f => f.value === feeType);
    return details?.label || feeType;
  } else {
    const details = ANALYST_FEE_TYPES.find(f => f.value === feeType);
    return details?.label || feeType;
  }
}

/**
 * Get recommended fee types by client type or use case
 */
export function getRecommendedAdvisorFeeTypes(clientProfile: {
  assetSize?: 'small' | 'medium' | 'large';
  needType?: 'ongoing' | 'one-time' | 'occasional';
}): AdvisorFeeType[] {
  const { assetSize, needType } = clientProfile;
  
  // Recommend based on asset size
  if (assetSize === 'large') return ['aum', 'hybrid', 'performance_based'];
  if (assetSize === 'medium') return ['aum', 'flat_fee', 'hybrid'];
  if (assetSize === 'small') return ['flat_fee', 'hourly', 'subscription'];
  
  // Recommend based on need type
  if (needType === 'ongoing') return ['aum', 'flat_fee', 'subscription'];
  if (needType === 'one-time') return ['project_based', 'hourly'];
  if (needType === 'occasional') return ['hourly', 'project_based', 'subscription'];
  
  return ['aum', 'flat_fee', 'hourly'];
}

/**
 * Get recommended analyst fee types by usage pattern
 */
export function getRecommendedAnalystFeeTypes(usageProfile: {
  frequency?: 'rare' | 'occasional' | 'frequent' | 'heavy';
  researchType?: 'standard' | 'custom' | 'data-driven';
}): AnalystFeeType[] {
  const { frequency, researchType } = usageProfile;
  
  // Recommend based on frequency
  if (frequency === 'rare') return ['per_request', 'freemium'];
  if (frequency === 'occasional') return ['per_request', 'tiered_subscription'];
  if (frequency === 'frequent') return ['subscription', 'tiered_subscription', 'hybrid'];
  if (frequency === 'heavy') return ['retainer', 'subscription', 'data_feed'];
  
  // Recommend based on research type
  if (researchType === 'standard') return ['subscription', 'tiered_subscription'];
  if (researchType === 'custom') return ['retainer', 'consulting_hourly', 'project_based'];
  if (researchType === 'data-driven') return ['data_feed', 'subscription'];
  
  return ['subscription', 'per_request'];
}

/**
 * Export all constants for easy access
 */
export const FinancialTaxonomy = {
  SPECIALIZATIONS: FINANCIAL_SPECIALIZATIONS,
  RISK_TOLERANCE: RISK_TOLERANCE_LEVELS,
  TIME_HORIZONS,
  CERTIFICATIONS,
  RESEARCH_STYLES,
  SECTORS,
  ADVISOR_FEE_TYPES,
  ANALYST_FEE_TYPES,
  INVESTMENT_EXPERIENCE_LEVELS,

   // Matching filter options
  SPECIALIZATION_FILTERS: SPECIALIZATION_FILTER_OPTIONS,
  CERTIFICATION_FILTERS: CERTIFICATION_FILTER_OPTIONS,
  SECTOR_FILTERS: SECTOR_FILTER_OPTIONS,
  LOCATION_FILTERS: LOCATION_FILTER_OPTIONS,
  RATING_FILTERS: RATING_FILTER_OPTIONS,
  MATCH_SCORE_SLIDER,
  
  // Helper functions
  getDescription: getSpecializationDescription,
  getPremiumCerts: getPremiumCertifications,
  isPremiumCert: isPremiumCertification,
  formatForBackend: formatSpecializationsForBackend,
  parseFromBackend: parseSpecializationsFromBackend,
  isValid: isValidSpecialization,
  getCategory: getSpecializationCategory,
  getRecommended: getRecommendedSpecializations,
  getAdvisorFeeTypeDetails,
  getAnalystFeeTypeDetails,
  formatFeeTypeLabel,
  getRecommendedAdvisorFeeTypes,
  getRecommendedAnalystFeeTypes,
} as const;


export default FinancialTaxonomy;

/**
 * ============================================================
 * AI QUICK ACTION PARAMETERS
 * ============================================================
 * Types and interfaces for AI-powered quick actions
 */

/**
 * Quick Action Preferences
 * Tracks which AI quick actions are enabled/disabled for a user
 */
export interface QuickActionPreferences {
  portfolioReport: boolean;
  portfolioInsightsTasks: boolean;
  // scheduleMeeting: boolean; // Hidden for now - revisit later
  investmentProposal: boolean;
  riskAnalysis: boolean;
  financialGoals: boolean;
  marketInsights: boolean;
  financialPlan: boolean;
  rebalancePlan: boolean;
  retirementTaxPlan: boolean;
  estateRiskPlan: boolean;
}

/**
 * Quick Action History
 * Tracks which actions have been run at least once
 */
export interface QuickActionHistory {
  portfolioReport: boolean;
  portfolioInsightsTasks: boolean;
  // scheduleMeeting: boolean; // Hidden for now - revisit later
  investmentProposal: boolean;
  riskAnalysis: boolean;
  financialGoals: boolean;
  marketInsights: boolean;
  financialPlan: boolean;
  rebalancePlan: boolean;
  retirementTaxPlan: boolean;
  estateRiskPlan: boolean;
}

/**
 * Quick Action Report Status
 * Status of a generated report
 */
export type QuickActionReportStatus = 'processing' | 'ready' | 'error' | 'none';

/**
 * Quick Action Report Metadata
 * Information about a generated report
 */
export interface QuickActionReport {
  filename: string;
  generatedAt: string;
  status: QuickActionReportStatus;
}

/**
 * Quick Action Reports Collection
 * Stores all generated reports for a user
 */
export interface QuickActionReports {
  portfolioReport: QuickActionReport | null;
  portfolioInsightsTasks: QuickActionReport | null;
  // scheduleMeeting: QuickActionReport | null; // Hidden for now - revisit later
  investmentProposal: QuickActionReport | null;
  riskAnalysis: QuickActionReport | null;
  financialGoals: QuickActionReport | null;
  marketInsights: QuickActionReport | null;
  financialPlan: QuickActionReport | null;
  rebalancePlan: QuickActionReport | null;
  retirementTaxPlan: QuickActionReport | null;
  estateRiskPlan: QuickActionReport | null;
}

/**
 * Quick Action Types
 * Available AI quick action types
 */
export const QUICK_ACTION_TYPES = [
  'portfolioReport',
  'portfolioInsightsTasks',
  // 'scheduleMeeting', // Hidden for now - revisit later
  'investmentProposal',
  'riskAnalysis',
  'financialGoals',
  'marketInsights',
  'financialPlan',
  'rebalancePlan',
  'retirementTaxPlan',
  'estateRiskPlan',
] as const;

export type QuickActionType = typeof QUICK_ACTION_TYPES[number];

/**
 * AI Structured Formats
 * Available formats for AI responses and reports
 */
export const AI_STRUCTURED_FORMATS = [
  { value: "text", label: "Plain Text" },
  { value: "bullet_points", label: "Bullet Points" },
  { value: "table", label: "Table" },
  { value: "json", label: "JSON" },
  { value: "detailed_report", label: "Detailed Report" },
  { value: "executive_summary", label: "Executive Summary" },
] as const;

export type AIStructuredFormat = typeof AI_STRUCTURED_FORMATS[number]['value'];

/**
 * Quick Action Metadata
 * Descriptive information about each quick action
 */
export const QUICK_ACTION_METADATA = {
  portfolioReport: {
    name: 'Portfolio Report',
    description: 'Comprehensive portfolio analysis with performance metrics, asset allocation, and recommendations',
    icon: 'TrendingUp',
    prompt: 'Generate a comprehensive portfolio analysis report including: 1) Overall portfolio performance vs benchmark, 2) Asset allocation breakdown, 3) Sector diversification analysis, 4) Top performing and underperforming holdings, 5) Risk metrics (beta, volatility, Sharpe ratio), 6) Rebalancing recommendations, 7) Cost analysis (fees, expense ratios), 8) Tax optimization opportunities. Format as professional PDF with charts and actionable insights.',
  },
  portfolioInsightsTasks: {
    name: 'Portfolio Insights and Tasks',
    description: 'Generate actionable recommended tasks and insights from portfolio data and goals',
    icon: 'CheckCircle',
    prompt: 'Analyze the portfolio data and generate actionable tasks and insights including: 1) Immediate actions needed (e.g., rebalancing, tax-loss harvesting), 2) Long-term strategic recommendations, 3) Risk mitigation strategies, 4) Opportunity identification, 5) Performance improvement suggestions. Format as a structured task list with priorities and timelines based on following format:' +
    '['+
      'interface AIInsight {'+
        'id: string;'+
        'type: "recommendation" | "alert" | "opportunity" | "analysis";'+
        'title: string;'+
        'description: string;'+
        'details: string;'+
        'confidence: number;'+
        'relevantTo: ["investor", "analyst", "advisor"];'+
        'timestamp: string;'+
      '},'+
      'interface Task {'+
        'id: string;'+
        'title: string;'+
        'priority: "high" | "medium" | "low";'+
        'timestamp: string;'+
      '}'+
    ']'
  },
  /* scheduleMeeting: {
    name: 'Schedule Meeting',
    description: 'Team meeting invitation with pre-populated agenda and attendee list',
    icon: 'Calendar',
    prompt: 'Create a professional meeting invitation including: 1) Clear meeting purpose and objectives, 2) Recommended duration and timing options, 3) Suggested attendee list with roles, 4) Pre-populated agenda with key topics, 5) Required preparation materials, 6) Meeting location/virtual platform details, 7) Follow-up action items template. Format as calendar-ready invitation with professional tone.',
  }, */
  investmentProposal: {
    name: 'Investment Proposal',
    description: 'Detailed investment recommendations with expected returns and risk analysis',
    icon: 'FileText',
    prompt: 'Generate a detailed investment proposal including: 1) Investment objectives and strategy, 2) Specific security/asset recommendations with allocation percentages, 3) Expected returns and risk metrics, 4) Time horizon and exit strategy, 5) Cost structure and fees, 6) Risk mitigation strategies, 7) Performance projections under different market scenarios, 8) Implementation timeline. Format as professional proposal with supporting data and charts.',
  },
  riskAnalysis: {
    name: 'Risk Analysis',
    description: 'Complete risk assessment with portfolio metrics and stress test scenarios',
    icon: 'BarChart3',
    prompt: 'Conduct comprehensive risk analysis including: 1) Current risk profile assessment, 2) Portfolio-level risk metrics (VaR, CVaR, maximum drawdown), 3) Concentration risk analysis, 4) Stress testing under various market scenarios, 5) Correlation analysis and diversification benefits, 6) Geographic and sector risk exposure, 7) Liquidity risk assessment, 8) Recommendations for risk optimization. Include visual risk charts and actionable risk management strategies.',
  },
  financialGoals: {
    name: 'Financial Goals',
    description: 'Progress review on short, medium, and long-term financial goals',
    icon: 'Target',
    prompt: 'Generate financial goals progress report including: 1) Review of established financial goals (short, medium, long-term), 2) Current progress tracking with milestones achieved, 3) Gap analysis between current trajectory and goals, 4) Required savings/investment rates to meet goals, 5) Goal probability analysis under different scenarios, 6) Recommended adjustments to strategy, 7) Timeline updates and revised milestones, 8) Action plan for next 12 months. Include progress charts and specific recommendations.',
  },
  marketInsights: {
    name: 'Market Insights',
    description: 'Current market analysis with sector performance and investment opportunities',
    icon: 'Brain',
    prompt: 'Generate comprehensive market insights report including: 1) Current market overview and key trends, 2) Economic indicators and their impact, 3) Sector performance analysis and rankings, 4) Investment opportunities and risks by sector, 5) Market sentiment indicators, 6) Technical analysis of major indices, 7) Geopolitical factors affecting markets, 8) Recommended positioning strategies. Include market charts, sector heat maps, and actionable investment ideas.',
  },
  financialPlan: {
    name: 'Financial Plan',
    description: 'Cash flow, savings goals, and budget plan for the client',
    icon: 'Target',
    prompt: 'Build a financial plan for the client: assess cash flow, set savings goals (e.g. home, college, retirement), and create a budget. Use the portfolio and onboarding data provided.',
  },
  rebalancePlan: {
    name: 'Rebalance Plan',
    description: 'Allocation drift and proposed trades to restore the target allocation',
    icon: 'TrendingUp',
    prompt: 'Produce a portfolio rebalancing plan for the client: show target vs current allocation, drift, and proposed trades mapped to holdings.',
  },
  retirementTaxPlan: {
    name: 'Retirement & Tax Plan',
    description: 'Monte Carlo retirement projection, withdrawal strategy, and tax harvesting',
    icon: 'Brain',
    prompt: 'Create a retirement and tax plan for the client: run a Monte Carlo projection, recommend a withdrawal order, and suggest tax-loss harvesting opportunities.',
  },
  estateRiskPlan: {
    name: 'Estate & Risk Plan',
    description: 'Net worth, insurance coverage gaps, and estate plan draft',
    icon: 'Users',
    prompt: 'Draft an estate and risk plan for the client: summarize net worth, assess life/disability/LTC insurance gaps, and outline an estate plan (beneficiaries, vehicles).',
  },
} as const;

/**
 * Default Quick Action Preferences
 * All actions disabled by default
 */
export const DEFAULT_QUICK_ACTION_PREFERENCES: QuickActionPreferences = {
  portfolioReport: false,
  portfolioInsightsTasks: false,
  investmentProposal: false,
  riskAnalysis: false,
  financialGoals: false,
  marketInsights: false,
  financialPlan: false,
  rebalancePlan: false,
  retirementTaxPlan: false,
  estateRiskPlan: false,
};

/**
 * Default Quick Action History
 * No actions run by default
 */
export const DEFAULT_QUICK_ACTION_HISTORY: QuickActionHistory = {
  portfolioReport: false,
  portfolioInsightsTasks: false,
  investmentProposal: false,
  riskAnalysis: false,
  financialGoals: false,
  marketInsights: false,
  financialPlan: false,
  rebalancePlan: false,
  retirementTaxPlan: false,
  estateRiskPlan: false,
};

/**
 * Default Quick Action Reports
 * No reports generated by default
 */
export const DEFAULT_QUICK_ACTION_REPORTS: QuickActionReports = {
  portfolioReport: null,
  portfolioInsightsTasks: null,
  investmentProposal: null,
  riskAnalysis: null,
  financialGoals: null,
  marketInsights: null,
  financialPlan: null,
  rebalancePlan: null,
  retirementTaxPlan: null,
  estateRiskPlan: null,
};

/**
 * Quick Action Helper Functions
 */

/**
 * Get quick action metadata by type
 */
export function getQuickActionMetadata(actionType: QuickActionType) {
  return QUICK_ACTION_METADATA[actionType];
}

/**
 * Check if a quick action is enabled
 */
export function isQuickActionEnabled(
  actionType: QuickActionType,
  preferences: QuickActionPreferences
): boolean {
  return preferences[actionType];
}

/**
 * Check if a quick action has been run
 */
export function hasQuickActionBeenRun(
  actionType: QuickActionType,
  history: QuickActionHistory
): boolean {
  return history[actionType];
}

/**
 * Get enabled quick actions
 */
export function getEnabledQuickActions(preferences: QuickActionPreferences): QuickActionType[] {
  return QUICK_ACTION_TYPES.filter(action => preferences[action]);
}

/**
 * Get report status for an action
 */
export function getQuickActionReportStatus(
  actionType: QuickActionType,
  reports: QuickActionReports
): QuickActionReportStatus | null {
  const report = reports[actionType];
  return report ? report.status : null;
}

/**
 * Research Hierarchy
 * Defines the structure for AI-powered financial research and analysis
 */
export const RESEARCH_HIERARCHY = {
  PORTFOLIO_ANALYSIS: {
    description: "Analyze investment portfolios for performance, risk, and optimization",
    analysisTypes: {
      COMPREHENSIVE: {
        description: "Full portfolio review",
        defaultQuestionnaires: [
          "Provide a complete overview of the portfolio's current state",
          "What are the portfolio's strengths and weaknesses?",
          "How well is the portfolio diversified across asset classes?",
          "What are the top 3 recommendations to improve this portfolio?",
          "How does this portfolio align with typical investment goals?"
        ]
      },
      PERFORMANCE: {
        description: "Returns, benchmarks, alpha/beta",
        defaultQuestionnaires: [
          "What is the portfolio's total return over the selected timeframe?",
          "How does performance compare to the benchmark?",
          "What are the top and bottom performing holdings?",
          "What is the portfolio's Sharpe ratio and risk-adjusted return?",
          "Identify periods of underperformance and potential causes"
        ]
      },
      RISK: {
        description: "Volatility, drawdowns, VaR",
        defaultQuestionnaires: [
          "What is the portfolio's overall volatility?",
          "What is the maximum drawdown history?",
          "What is the Value at Risk (VaR) at 95% confidence?",
          "How correlated are the holdings?",
          "What scenarios would cause significant losses?"
        ]
      },
      ALLOCATION: {
        description: "Asset class distribution",
        defaultQuestionnaires: [
          "What is the current asset allocation breakdown?",
          "How does the allocation compare to target allocations?",
          "Which asset classes are over/under-weighted?",
          "What is the geographic distribution?",
          "How should the allocation be rebalanced?"
        ]
      },
      DIVERSIFICATION: {
        description: "Correlation and concentration analysis",
        defaultQuestionnaires: [
          "How diversified is the portfolio across sectors?",
          "What is the concentration risk in top holdings?",
          "How correlated are the portfolio's investments?",
          "Are there any single points of failure?",
          "What diversification improvements are recommended?"
        ]
      }
    }
  },
  MARKET_RESEARCH: {
    description: "Research market trends, sectors, and economic indicators",
    analysisTypes: {
      SECTOR_ANALYSIS: {
        description: "Industry-specific research",
        defaultQuestionnaires: [
          "What are the current trends in this sector?",
          "Which companies are leading the sector?",
          "What are the key risks facing this sector?",
          "How has this sector performed vs. the broader market?",
          "What regulatory changes could impact this sector?"
        ]
      },
      MACROECONOMIC: {
        description: "Economic indicators and trends",
        defaultQuestionnaires: [
          "What are the current GDP growth projections?",
          "How is inflation trending and what's the impact?",
          "What is the current interest rate environment?",
          "How are employment trends affecting the market?",
          "What are the key geopolitical risks?"
        ]
      },
      TECHNICAL: {
        description: "Chart patterns and indicators",
        defaultQuestionnaires: [
          "What are the key support and resistance levels?",
          "What chart patterns are currently forming?",
          "What do moving averages indicate about trend direction?",
          "What is the current momentum signal?",
          "What do volume patterns suggest?"
        ]
      },
      SENTIMENT: {
        description: "News and social sentiment analysis",
        defaultQuestionnaires: [
          "What is the overall market sentiment (bullish/bearish)?",
          "How has sentiment changed over the past month?",
          "What are the most discussed topics in financial news?",
          "How does social media sentiment compare to analyst ratings?",
          "What sentiment shifts are on the horizon?"
        ]
      }
    }
  },
  SECURITY_ANALYSIS: {
    description: "Deep dive into individual stocks, bonds, or assets",
    analysisTypes: {
      FUNDAMENTAL: {
        description: "Financial statements, ratios",
        defaultQuestionnaires: [
          "What is the company's revenue growth trend?",
          "How healthy are the profit margins?",
          "What is the debt-to-equity ratio and financial health?",
          "How does the company generate cash flow?",
          "What are the key competitive advantages?"
        ]
      },
      VALUATION: {
        description: "DCF, multiples, intrinsic value",
        defaultQuestionnaires: [
          "What is the intrinsic value using DCF analysis?",
          "How does current valuation compare to historical multiples?",
          "What is the P/E ratio relative to peers?",
          "What is the price-to-book ratio indicating?",
          "Is the stock overvalued or undervalued?"
        ]
      },
      TECHNICAL: {
        description: "Price action, volume analysis",
        defaultQuestionnaires: [
          "What is the current price trend?",
          "What are the key technical indicators signaling?",
          "How is trading volume behaving?",
          "Are there any breakout or breakdown patterns?",
          "What are the near-term price targets?"
        ]
      },
      COMPARATIVE: {
        description: "Peer analysis",
        defaultQuestionnaires: [
          "How does this company compare to its top 5 peers?",
          "What metrics does this company lead in?",
          "Where does it lag behind competitors?",
          "What is the relative valuation vs. peers?",
          "Which peer offers the best investment opportunity?"
        ]
      }
    }
  },
  RISK_ASSESSMENT: {
    description: "Evaluate portfolio and market risks",
    analysisTypes: {
      PORTFOLIO_RISK: {
        description: "Overall risk metrics",
        defaultQuestionnaires: [
          "What is the portfolio's overall volatility?",
          "What is the maximum drawdown history?",
          "How correlated are the holdings?",
          "What is the Value at Risk (VaR) at 95% confidence?",
          "What scenarios would cause significant losses?"
        ]
      },
      CONCENTRATION_RISK: {
        description: "Overexposure analysis",
        defaultQuestionnaires: [
          "What percentage of the portfolio is in the top 10 holdings?",
          "Is there overexposure to any single sector?",
          "What is the geographic concentration?",
          "Are there any currency concentration risks?",
          "How can concentration be reduced?"
        ]
      },
      MARKET_RISK: {
        description: "Systematic risk factors",
        defaultQuestionnaires: [
          "What is the portfolio's beta to the market?",
          "How sensitive is the portfolio to interest rate changes?",
          "What is the exposure to inflation risk?",
          "How would a market crash affect this portfolio?",
          "What hedging strategies are available?"
        ]
      },
      LIQUIDITY_RISK: {
        description: "Cash flow and liquidity",
        defaultQuestionnaires: [
          "How liquid are the portfolio's holdings?",
          "What is the average daily trading volume of holdings?",
          "Are there any illiquid assets that could be hard to sell?",
          "What is the cash position and runway?",
          "How quickly can the portfolio be converted to cash?"
        ]
      }
    }
  },
  OPPORTUNITY_DISCOVERY: {
    description: "Identify investment opportunities and recommendations",
    analysisTypes: {
      VALUE_OPPORTUNITIES: {
        description: "Undervalued assets",
        defaultQuestionnaires: [
          "Which assets are trading below intrinsic value?",
          "What catalysts could unlock value?",
          "What is the margin of safety?",
          "What are the potential upside scenarios?",
          "What are the key risks to the investment thesis?"
        ]
      },
      GROWTH_OPPORTUNITIES: {
        description: "High-growth prospects",
        defaultQuestionnaires: [
          "Which sectors/stocks show the highest growth potential?",
          "What are the revenue growth projections?",
          "What competitive advantages support growth?",
          "What is the market opportunity size?",
          "What are the key growth drivers?"
        ]
      },
      INCOME_OPPORTUNITIES: {
        description: "Dividend/yield focused",
        defaultQuestionnaires: [
          "Which assets offer the highest sustainable yields?",
          "What is the dividend growth history?",
          "Are the dividends well-covered by earnings?",
          "What is the yield relative to peers?",
          "What are the risks to dividend payments?"
        ]
      },
      MOMENTUM_OPPORTUNITIES: {
        description: "Trend-following",
        defaultQuestionnaires: [
          "Which assets are showing strong upward momentum?",
          "What are the current trend indicators?",
          "How long has the current trend been in place?",
          "What volume supports the momentum?",
          "When might the momentum reverse?"
        ]
      }
    }
  }
} as const;

export type ResearchCategory = keyof typeof RESEARCH_HIERARCHY;
export type AnalysisType = keyof typeof RESEARCH_HIERARCHY[ResearchCategory]['analysisTypes'];

export const ACCOUNT_TYPES = {
  BROKERAGE: { name: "Brokerage", category: "Taxable" },
  CASH_MANAGEMENT: { name: "Cash Management", category: "Cash" },
  TRADITIONAL_IRA: { name: "Traditional IRA", category: "Retirement" },
  ROTH_IRA: { name: "Roth IRA", category: "Retirement" },
  SEP_IRA: { name: "SEP IRA", category: "Retirement" },
  SIMPLE_IRA: { name: "SIMPLE IRA", category: "Retirement" },
  TRADITIONAL_401K: { name: "Traditional 401(k)", category: "Retirement" },
  ROTH_401K: { name: "Roth 401(k)", category: "Retirement" },
  TRADITIONAL_403B: { name: "Traditional 403(b)", category: "Retirement" },
  ROTH_403B: { name: "Roth 403(b)", category: "Retirement" },
  TRADITIONAL_457: { name: "Traditional 457", category: "Retirement" },
  PLAN_529: { name: "529 College Savings", category: "Education" },
  COVERDELL_ESA: { name: "Coverdell ESA", category: "Education" },
  HSA: { name: "Health Savings Account", category: "Health" },
  INHERITED_IRA: { name: "Inherited IRA", category: "Retirement" },
  ROLLOVER_IRA: { name: "Rollover IRA", category: "Retirement" },
  TRUST: { name: "Trust Account", category: "Taxable" },
} as const;

export const OWNERSHIP_TYPES = {
  INDIVIDUAL: "Individual",
  JOINT_WROS: "Joint - Right of Survivorship",
  JOINT_TIC: "Joint - Tenants in Common",
  TRUST: "Trust",
  ENTITY_LLC: "Entity - LLC",
  ENTITY_CORP: "Entity - Corporation",
  ENTITY_PARTNERSHIP: "Entity - Partnership",
  CUSTODIAL_UTMA: "Custodial - UTMA",
  CUSTODIAL_UGMA: "Custodial - UGMA",
} as const;

export const MEETING_TYPES = [
  { value: "intro", label: "Introduction Call" },
  { value: "consultation", label: "Full Consultation" },
  { value: "portfolio", label: "Portfolio Review" },
  { value: "research", label: "Research Deep-dive" },
] as const;

export const ACTIVITY_TYPES_ADVISOR: Record<string, string> = {
  MEETING_SCHEDULED: "Meeting Scheduled",
  ENGAGEMENT_REQUEST_SENT: "Engagement Request Sent",
  PORTFOLIO_REVIEW_COMPLETED: "Portfolio Review Completed",
  PORTFOLIO_REBALANCED: "Portfolio Rebalanced",
  PORTFOLIO_CREATED: "Portfolio Created",
  CONNECTION_REQUEST_SENT: "Connection Request Sent",
  PORTFOLIO_SHARED: "Portfolio Shared",
  DOCUMENT_UPLOADED: "Document Uploaded",
  DOCUMENT_DOWNLOADED: "Document Downloaded",
  TASK_COMPLETED: "Task Completed",
  SERVICE_REQUEST_RESPONDED: "Service Request Responded",
  EMAIL_SENT: "Email Sent",
};

export type AccountTypeKey = keyof typeof ACCOUNT_TYPES;
export type OwnershipTypeKey = keyof typeof OWNERSHIP_TYPES;
